import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Users, Calendar, TrendingUp, ArrowRight, Star, Sparkles, GraduationCap, MessageCircle, ShoppingBag, MessageSquare, Clock, MapPin, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner, CardSkeleton } from '@/components/ui/LoadingSpinner';

import ChampionsWall from '../components/ChampionsWall';
import ResultsStories from '../components/ResultsStories';
import OnlineUsers from '../components/OnlineUsers';
import NextTournamentWidget from '../components/NextTournamentWidget';
import MomentsGallery from '../components/MomentsGallery';
import { GearShowcase } from '../components/GearShowcase';
import { PartnerShowcase } from '../components/PartnerShowcase';
import { TrainingCampsSection } from '../components/TrainingCampsSection';
import { WhatsAppCommunitySection } from '../components/WhatsAppCommunitySection';
import { CoachesShowcase } from '../components/CoachesShowcase';
import { HomeSectionsEditor } from '../components/HomeSectionsEditor';
import { ActivityFeed } from '../components/ActivityFeed';
import { LiveDayBanner } from '../components/LiveDayBanner';
import { Settings } from 'lucide-react';

const HomePage = () => {
  const { API, user } = useContext(AuthContext);
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalCoaches, setTotalCoaches] = useState(0);
  const [totalTournaments, setTotalTournaments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sectionOrder, setSectionOrder] = useState(['partners', 'gear', 'coaches', 'camps', 'whatsapp']);
  const [orderEditorOpen, setOrderEditorOpen] = useState(false);

  const sectionComponents = {
    partners: <PartnerShowcase />,
    gear: <GearShowcase />,
    coaches: <CoachesShowcase />,
    camps: <TrainingCampsSection />,
    whatsapp: <WhatsAppCommunitySection />,
  };

  useEffect(() => {
    axios.get(`${API}/homepage-sections`)
      .then((res) => { if (res.data.order?.length) setSectionOrder(res.data.order); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUpcomingTournaments();
    fetchTotalUsers();
  }, []);

  const fetchUpcomingTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      const now = new Date();
      
      // מיון וסינון תחרויות
      const allTournaments = response.data;
      
      // תחרויות פעילות (in_progress)
      const activeTournaments = allTournaments.filter(t => t.status === 'in_progress');
      
      // תחרויות פתוחות להרשמה
      const openTournaments = allTournaments.filter(t => t.status === 'open' || t.status === 'full');
      
      // תחרויות עתידיות (לפי תאריך)
      const futureTournaments = allTournaments
        .filter(t => new Date(t.date) >= now && t.status !== 'completed')
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // שילוב - קודם פעילות, אח"כ פתוחות, אח"כ עתידיות
      const combined = [
        ...activeTournaments,
        ...openTournaments.filter(t => !activeTournaments.find(a => a.id === t.id)),
        ...futureTournaments.filter(t => 
          !activeTournaments.find(a => a.id === t.id) && 
          !openTournaments.find(o => o.id === t.id)
        )
      ].slice(0, 6);
      
      setUpcomingTournaments(combined);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalUsers = async () => {
    try {
      const response = await axios.get(`${API}/stats/total-users`);
      setTotalUsers(response.data.total_users);
      setTotalCoaches(response.data.total_coaches || 0);
      setTotalTournaments(response.data.completed_tournaments || response.data.total_tournaments || 0);
    } catch (error) {
      console.error('Failed to fetch total users:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      {/* Hero Section - Stadium court background */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundImage: "url('/hero-court-bg.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
        }}
        data-testid="hero-section"
      >
        {/* Soft light overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-white/10 to-white/40 pointer-events-none" />
        <div className="absolute top-1/3 left-10 text-6xl opacity-10 animate-bounce pointer-events-none hidden md:block" style={{ animationDuration: '3s' }}>🎾</div>
        <div className="absolute top-1/4 right-10 text-6xl opacity-10 animate-bounce pointer-events-none hidden md:block" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>🏆</div>
        <div className="absolute bottom-1/4 right-1/4 text-5xl opacity-10 animate-bounce pointer-events-none hidden md:block" style={{ animationDuration: '3.5s', animationDelay: '1s' }}>🎾</div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          {/* Wordmark - Tennis Pro Israel */}
          <div className="flex justify-center mb-8 animate-fade-in-up">
            <div className="relative inline-block group">
              {/* Glow halo */}
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-500/20 via-cyan-400/20 to-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative flex flex-col items-center" data-testid="hero-wordmark">
                {/* Swoosh curves + 3D tennis ball */}
                <div className="relative w-full flex justify-center mb-1 sm:mb-2" data-testid="hero-swoosh">
                  <svg viewBox="0 0 700 70" className="w-full max-w-[340px] sm:max-w-[560px] lg:max-w-[680px] h-auto absolute bottom-0" fill="none">
                    <defs>
                      <linearGradient id="swooshBlue" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#1d6ff2" />
                        <stop offset="1" stopColor="#1d6ff2" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="swooshGreen" x1="1" y1="0" x2="0" y2="0">
                        <stop offset="0" stopColor="#10b47e" />
                        <stop offset="1" stopColor="#10b47e" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M15 58 Q 160 14 300 46" stroke="url(#swooshBlue)" strokeWidth="6" strokeLinecap="round" />
                    <path d="M685 58 Q 540 14 400 46" stroke="url(#swooshGreen)" strokeWidth="6" strokeLinecap="round" />
                  </svg>
                  <img
                    src="/tennis-ball-3d.png"
                    alt=""
                    className="relative w-14 h-14 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain drop-shadow-xl -mb-2 sm:-mb-3"
                    data-testid="hero-tennis-ball"
                  />
                </div>

                {/* Main wordmark - Tennis·pro */}
                <h2
                  data-testid="hero-wordmark-text"
                  className="text-center leading-none"
                  dir="ltr"
                >
                  <span
                    className="block text-6xl sm:text-8xl lg:text-9xl"
                    style={{ fontFamily: "'Baloo 2', 'Outfit', sans-serif", fontWeight: 800, color: '#1e2b5e', letterSpacing: '-0.015em' }}
                  >
                    Tennis<span style={{ color: '#1d6ff2', margin: '0 0.06em' }}>·</span>Pro
                  </span>
                </h2>

                {/* ISRAEL with flanking gradient lines */}
                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-2 sm:mt-3 w-full" dir="ltr">
                  <div className="flex items-center flex-1 max-w-[70px] sm:max-w-[130px]">
                    <div className="h-[3px] sm:h-[4px] flex-1 rounded-full bg-gradient-to-r from-transparent to-[#1d6ff2]" />
                    <div className="h-3 sm:h-4 w-[3px] sm:w-[4px] rounded-full bg-[#1d6ff2] ml-1" />
                  </div>
                  <span
                    className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#1d6ff2] via-[#0ea5b7] to-[#10b47e] bg-clip-text text-transparent whitespace-nowrap"
                    style={{ fontFamily: "'Baloo 2', 'Outfit', sans-serif", letterSpacing: '0.45em', textIndent: '0.45em' }}
                  >
                    ISRAEL
                  </span>
                  <div className="flex items-center flex-1 max-w-[70px] sm:max-w-[130px]">
                    <div className="h-3 sm:h-4 w-[3px] sm:w-[4px] rounded-full bg-[#10b47e] mr-1" />
                    <div className="h-[3px] sm:h-[4px] flex-1 rounded-full bg-gradient-to-l from-transparent to-[#10b47e]" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 - Title */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-12 mb-6 border-2 border-blue-100 relative overflow-hidden" data-testid="hero-title-card">
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 animate-shimmer pointer-events-none" />

            <div className="relative">
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  קהילת הטניס המובילה בישראל
                </div>
              </div>
              <h1 className="text-center text-4xl sm:text-6xl lg:text-7xl font-black leading-tight mb-6" data-testid="hero-title">
                <span className="text-slate-900">קהילת הטניס</span>
                <br />
                <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">
                  הגדולה בישראל
                </span>
              </h1>
              <p className="text-center text-base sm:text-xl text-slate-600 font-medium" data-testid="hero-description">
                תחרויות יומיות · דירוג שחקנים · פרסים מדהימים · קהילה משגשגת
              </p>
            </div>
          </div>

          {/* Live Stats Card */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-3xl mx-auto mb-6" data-testid="hero-stats">
            <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-5 border border-blue-100 text-center group hover:-translate-y-1 transition-all">
              <Users className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600 mx-auto mb-1" />
              <div className="text-2xl sm:text-3xl font-black text-blue-600" data-testid="stats-users">{totalUsers}</div>
              <div className="text-xs sm:text-sm text-slate-600 font-medium">שחקנים פעילים</div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-5 border border-amber-100 text-center group hover:-translate-y-1 transition-all">
              <Trophy className="w-5 h-5 sm:w-7 sm:h-7 text-amber-500 mx-auto mb-1" />
              <div className="text-2xl sm:text-3xl font-black text-amber-600" data-testid="stats-tournaments">{totalTournaments}</div>
              <div className="text-xs sm:text-sm text-slate-600 font-medium">תחרויות</div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-5 border border-emerald-100 text-center group hover:-translate-y-1 transition-all">
              <GraduationCap className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-600 mx-auto mb-1" />
              <div className="text-2xl sm:text-3xl font-black text-emerald-600" data-testid="stats-coaches">{totalCoaches}</div>
              <div className="text-xs sm:text-sm text-slate-600 font-medium">מאמנים</div>
            </div>
          </div>

          {/* Card 3 - CTA Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {!user ? (
              <Link to="/auth" className="group" data-testid="hero-register-link">
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl p-6 shadow-2xl transition-all hover:-translate-y-1">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all" />
                  <div className="relative flex items-center justify-between">
                    <div className="text-right">
                      <div className="text-2xl font-black mb-1">הירשם עכשיו</div>
                      <div className="text-sm text-blue-100">הצטרף לקהילה</div>
                    </div>
                    <ArrowRight className="w-8 h-8" />
                  </div>
                </div>
              </Link>
            ) : (
              <Link to="/tournaments/daily" className="group" data-testid="hero-tournaments-link">
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl p-6 shadow-2xl transition-all hover:-translate-y-1">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all" />
                  <div className="relative flex items-center justify-between">
                    <div className="text-right">
                      <div className="text-2xl font-black mb-1">ראה תחרויות</div>
                      <div className="text-sm text-blue-100">צפה בכל התחרויות הפעילות</div>
                    </div>
                    <ArrowRight className="w-8 h-8" />
                  </div>
                </div>
              </Link>
            )}
            <Link to="/levels" className="group" data-testid="hero-levels-link">
              <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white rounded-2xl p-6 shadow-2xl transition-all hover:-translate-y-1">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all" />
                <div className="relative flex items-center justify-between">
                  <div className="text-right">
                    <div className="text-2xl font-black mb-1">מה הרמה שלי?</div>
                    <div className="text-sm text-emerald-100">בדוק את רמת המשחק שלך</div>
                  </div>
                  <Trophy className="w-8 h-8" />
                </div>
              </div>
            </Link>
          </div>
        </div>

        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%) skewX(-12deg); }
            100% { transform: translateX(200%) skewX(-12deg); }
          }
          .animate-shimmer { animation: shimmer 3s infinite; }
        `}</style>
      </div>

      {/* 📅 התחרות הבאה שלי */}
      <NextTournamentWidget />

      {/* 🟢 מחוברים עכשיו */}
      <div className="mt-6">
        <OnlineUsers />
      </div>

      {/* 🎬 סטורי תוצאות */}
      <ResultsStories />

      {/* 📸 רגעים מהמגרש */}
      <MomentsGallery />

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">למה לבחור בנו?</h2>
          <p className="text-gray-600 text-lg">הקהילה המובילה לשחקני טניס בישראל</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 stagger-animation">
          <Card className="card-hover glass-effect relative overflow-hidden" data-testid="feature-card-tournaments">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200 to-blue-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
            <CardHeader className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg animate-float">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">תחרויות יומיות</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <CardDescription className="text-base leading-relaxed text-gray-700">
                תחרויות שמתחילות ומסתיימות באותו היום. עד 16 שחקנים בכל קטגוריה.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="card-hover glass-effect relative overflow-hidden" data-testid="feature-card-knockout">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200 to-green-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
            <CardHeader className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg animate-float" style={{animationDelay: '0.2s'}}>
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">תחרויות נוקאאוט</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <CardDescription className="text-base leading-relaxed text-gray-700">
                תחרויות ארוכות עד 32 שחקנים עם מערכת נוקאאוט מלאה ופרסים גדולים.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="card-hover glass-effect relative overflow-hidden" data-testid="feature-card-ranking">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-200 to-yellow-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
            <CardHeader className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg animate-float" style={{animationDelay: '0.4s'}}>
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">דירוג שחקנים</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <CardDescription className="text-base leading-relaxed text-gray-700">
                מערכת דירוג מתעדכנת אחרי כל תחרות עם ניקוד וסטטיסטיקות מפורטות.
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Live Day Banner + פיד פעילות */}
        <LiveDayBanner />
        <ActivityFeed />

        {/* קטגוריות קהילה - סדר דינמי */}
        {user?.is_admin && (
          <div className="flex justify-end mt-8">
            <Button variant="outline" size="sm" onClick={() => setOrderEditorOpen(true)} className="rounded-full text-gray-500 border-dashed border-gray-300" data-testid="edit-sections-order-btn">
              <Settings className="w-4 h-4 ml-1" />
              ערוך סדר קטגוריות
            </Button>
          </div>
        )}
        {sectionOrder.map((key) => (
          <React.Fragment key={key}>{sectionComponents[key]}</React.Fragment>
        ))}
        <HomeSectionsEditor open={orderEditorOpen} onClose={() => setOrderEditorOpen(false)} order={sectionOrder} onSaved={setSectionOrder} />
      </div>

      {/* Upcoming Tournaments */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="upcoming-tournaments-title">
            🎾 תחרויות פעילות וקרובות
          </h2>
          <p className="text-lg text-gray-600">הצטרפו לתחרויות הטניס המרגשות ביותר בישראל</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="טוען תחרויות..." />
          </div>
        ) : upcomingTournaments.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {upcomingTournaments.map((tournament, index) => {
              const getStatusInfo = (t) => {
                if (t.status === 'upcoming') return { label: 'יפתח בקרוב', cls: 'bg-orange-500/10 text-orange-600 border border-orange-500/25', dot: 'bg-orange-500' };
                if (t.status === 'in_progress') return { label: 'LIVE עכשיו', cls: 'bg-red-500/10 text-red-600 border border-red-500/25 animate-pulse', dot: 'bg-red-500' };
                if (t.status === 'completed') return { label: 'הסתיים', cls: 'bg-slate-100 text-slate-500 border border-slate-200', dot: 'bg-slate-400' };
                if (t.status === 'full') return { label: 'מלא', cls: 'bg-red-500/10 text-red-600 border border-red-500/25', dot: 'bg-red-500' };
                return { label: 'פתוח להרשמה', cls: 'bg-[#10b47e]/10 text-[#0da06f] border border-[#10b47e]/25', dot: 'bg-[#10b47e]' };
              };
              const categoryAccent = (cat = '') => {
                if (cat.includes('דרג א')) return 'from-amber-400 to-orange-500';
                if (cat.includes('דרג ב')) return 'from-[#1d6ff2] to-cyan-400';
                if (cat.includes('דרג ג')) return 'from-violet-500 to-purple-400';
                if (cat.includes('זוגות')) return 'from-indigo-500 to-[#1d6ff2]';
                if (cat.includes('נשים')) return 'from-pink-500 to-rose-400';
                return 'from-[#10b47e] to-emerald-400';
              };
              const statusInfo = getStatusInfo(tournament);
              const isDoubles = tournament.category?.includes('זוגות');
              const regCount = isDoubles
                ? (tournament.registered_players_names?.length || Math.ceil((tournament.registered_players?.length || 0) / 2))
                : (tournament.registered_players?.length || 0);
              const maxCount = tournament.max_participants || tournament.max_players || 16;
              const fillPct = Math.min(100, Math.round((regCount / maxCount) * 100));
              const dateObj = new Date(tournament.date);

              return (
                <div key={tournament.id} className="list-item-animate" style={{ animationDelay: `${index * 0.06}s` }}>
                  <div
                    className="h-full bg-white dark:bg-slate-900 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1 hover:border-[#1d6ff2]/30 transition-all duration-300 group flex flex-col relative"
                    data-testid={`tournament-card-${tournament.id}`}
                  >
                    {/* Category accent strip */}
                    <div className={`h-1.5 w-full bg-gradient-to-l ${categoryAccent(tournament.category)}`} />

                    {/* Card header */}
                    <Link to={`/tournament/${tournament.id}`} className="block px-5 pt-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="shrink-0 w-16 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-center py-2">
                          <div className="text-2xl font-black text-[#1e2b5e] dark:text-white leading-none">{dateObj.getDate()}</div>
                          <div className="text-[11px] font-bold text-[#1d6ff2] mt-1">{dateObj.toLocaleDateString('he-IL', { month: 'short' })}</div>
                          <div className="text-[10px] text-slate-400">{dateObj.toLocaleDateString('he-IL', { weekday: 'short' })}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${statusInfo.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
                            {statusInfo.label}
                          </span>
                          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug mt-1.5 group-hover:text-[#1d6ff2] transition-colors line-clamp-2">
                            {tournament.name}
                          </h3>
                          <p className="text-sm text-slate-400 font-medium">{tournament.category} • {tournament.type === 'daily' ? 'יומית' : 'נוקאאוט'}</p>
                        </div>
                      </div>
                    </Link>

                    <div className="px-5 pb-5 pt-4 flex flex-col gap-3 flex-1">
                      {/* Time + location */}
                      <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 flex-wrap">
                        {tournament.start_time && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-[#1d6ff2]" />
                            <span className="font-bold">{tournament.start_time}</span>
                          </span>
                        )}
                        {tournament.location && (
                          <span className="flex items-center gap-1.5 min-w-0">
                            <MapPin className="w-4 h-4 text-[#10b47e] shrink-0" />
                            <span className="truncate">{tournament.location}</span>
                          </span>
                        )}
                      </div>

                      {/* Capacity progress */}
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                            <Users className="w-4 h-4 text-[#1e2b5e] dark:text-slate-300" />
                            {isDoubles ? 'זוגות' : 'משתתפים'}
                          </span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">
                            {regCount}<span className="text-slate-400 font-medium">/{maxCount}</span>
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${fillPct >= 100 ? 'bg-red-500' : fillPct >= 75 ? 'bg-gradient-to-l from-orange-400 to-amber-400' : 'bg-gradient-to-l from-[#10b47e] to-emerald-400'}`}
                            style={{ width: `${fillPct}%` }}
                          />
                        </div>
                      </div>

                      {/* Player chips - full list */}
                      {tournament.registered_players_names && tournament.registered_players_names.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid={`home-player-chips-${tournament.id}`}>
                          {tournament.registered_players_names.map((name, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                              {name}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Waiting list */}
                      {tournament.waiting_list && tournament.waiting_list.length > 0 && (
                        <div className="bg-orange-500/5 border border-orange-500/20 p-2.5 rounded-xl">
                          <div className="flex items-center justify-between text-orange-600 mb-1">
                            <span className="text-xs font-bold">⏳ רשימת המתנה</span>
                            <span className="font-black text-xs">{tournament.waiting_list.length}</span>
                          </div>
                          {tournament.waiting_list_names && tournament.waiting_list_names.length > 0 && (
                            <p className="text-xs text-orange-500/90 leading-relaxed">{tournament.waiting_list_names.join(', ')}</p>
                          )}
                        </div>
                      )}

                      {/* Action - pinned to bottom */}
                      <div className="mt-auto pt-1">
                        <Link to={`/tournament/${tournament.id}`}>
                          <Button className="w-full bg-[#1e2b5e] hover:bg-[#16204a] dark:bg-[#1d6ff2] dark:hover:bg-[#1660d8] text-white rounded-xl py-3 font-semibold transition-transform active:scale-[0.98]">
                            {tournament.status === 'open' ? 'פרטים והרשמה' : 'צפה בפרטים'}
                            <ChevronLeft className="w-4 h-4 mr-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white/50 rounded-2xl border-2 border-dashed border-gray-300">
            <div className="text-6xl mb-4">🎾</div>
            <p className="text-xl text-gray-600 mb-4">אין תחרויות פעילות כרגע</p>
            <Link to="/tournaments/daily">
              <Button className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600">
                צפה בכל התחרויות
              </Button>
            </Link>
          </div>
        )}
        
        {/* כפתור לכל התחרויות */}
        {upcomingTournaments.length > 0 && (
          <div className="text-center mt-8">
            <Link to="/tournaments/daily">
              <Button variant="outline" size="lg" className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50">
                צפה בכל התחרויות
                <ArrowRight className="w-5 h-5 mr-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Champions Wall */}
      <ChampionsWall />

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" data-testid="cta-title">
            מוכנים לשחק?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            הצטרפו אלינו והתחילו לשחק בתחרויות הטניס המרגשות ביותר בישראל
          </p>
          {!user && (
            <Link to="/auth" data-testid="cta-register-link">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6" data-testid="cta-register-btn">
                הירשם בחינם
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Trophy className="w-8 h-8" />
            <h3 className="text-2xl font-bold">Tennis Pro Israel</h3>
          </div>
          <p className="text-gray-400 mb-6">קהילת הטניס הגדולה בישראל</p>
          <div className="flex justify-center gap-6">
            <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
              צור קשר
            </Link>
            <Link to="/levels" className="text-gray-400 hover:text-white transition-colors">
              רמות
            </Link>
            <Link to="/rankings" className="text-gray-400 hover:text-white transition-colors">
              דירוג
            </Link>
          </div>
          <p className="text-gray-500 text-sm mt-8">&copy; 2024 Tennis Pro Israel. כל הזכויות שמורות.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
